import { useMemo } from 'react';
import type { OsobyFyzickaOsobaDto } from '@gov-nx/api';
import { useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import type { PageCode } from '@gov-nx/module/page';
import type { SimpleDataTableColumn } from '@gov-nx/ui/web';
import { SimpleDataTable } from '@gov-nx/ui/web';
import {
	extractCountryNames,
	nameFormatting,
	toDateReadable,
} from '@gov-nx/utils/common';

interface TableTypes {
	data: Nullable<OsobyFyzickaOsobaDto>;
	code: PageCode;
}

const DEFAULT_EMPTY_VALUE = '-';

export const ProfileROBDataView = ({ data, code }: TableTypes) => {
	const { t } = useLocale(code);

	const columns = useMemo<SimpleDataTableColumn<OsobyFyzickaOsobaDto>[]>(
		() => [
			{
				id: 'jmeno',
				label: t('zaznam.jmeno-prijmeni'),
				valueFormatter: ({ data }) =>
					data?.jmeno && data.prijmeni
						? nameFormatting(data?.jmeno, data?.prijmeni)
						: DEFAULT_EMPTY_VALUE,
			},
			{
				id: 'rodnePrijmeni',
				label: t('zaznam.rodne-prijmeni'),
				valueFormatter: ({ value }) =>
					value ? nameFormatting(value) : DEFAULT_EMPTY_VALUE,
			},
			{
				id: 'statniObcanstvi',
				label: t('zaznam.statni-obcanstvi'),

				valueFormatter: ({ value }) =>
					value ? extractCountryNames(value) : DEFAULT_EMPTY_VALUE,
			},
			{
				id: 'rodinnyStavPartnerstvi',
				label: t('zaznam.rodinny-stav'),
				valueFormatter: ({ data }) =>
					data?.rodinnyStavPartnerstvi
						? t(
								'zaznam.rodinny-stav-moznosti.' +
									data?.rodinnyStavPartnerstvi?.replace(/\/|\s/g, '-')
							)
						: DEFAULT_EMPTY_VALUE,
			},
			{
				id: 'pohlavi',
				label: t('zaznam.pohlavi'),
				valueFormatter: ({ value }) =>
					value ? t('zaznam.pohlavi-moznosti.' + value) : DEFAULT_EMPTY_VALUE,
			},
			{
				id: 'datumNarozeni',
				label: t('zaznam.datum-narozeni'),
				valueFormatter: ({ value }) =>
					value ? toDateReadable(value) : DEFAULT_EMPTY_VALUE,
			},
			{ id: 'mistoNarozeni', label: t('zaznam.misto-narozeni') },
			{
				id: 'adresaPobytu',
				label: t('zaznam.adresa-mista-pobytu'),
				valueFormatter: ({ data }) =>
					data?.adresaPobytu?.adresaText ?? DEFAULT_EMPTY_VALUE,
			},
			{
				id: 'adresaDorucovaci',
				label: t('zaznam.dorucovaci-adresa'),
				valueFormatter: ({ data }) =>
					data?.adresaDorucovaci?.adresaText ?? DEFAULT_EMPTY_VALUE,
			},
			{
				id: 'omezeniSvepravnosti',
				label: t('zaznam.omezeni-svepravnosti'),
				valueFormatter: ({ value }) =>
					value
						? t('zaznam.omezeni-svepravnosti-status.ano')
						: t('zaznam.omezeni-svepravnosti-status.ne'),
			},
			{ id: 'datovaSchrankaId', label: t('zaznam.datova-schranka') },
		],
		[t]
	);

	return (
		<SimpleDataTable
			columns={columns}
			data={data ?? {}}
		/>
	);
};
